/**
 * BrokerInstructions Component
 *
 * Displays instructions for obtaining API credentials from the broker's website
 * Provides guided setup including app name and redirect URL configuration
 *
 * This component has been moved from Gateway to eliminate dependencies
 * and make it available for the AddOrEditTradeAccount workflow.
 */

import React, { useState, useEffect } from "react";
import { services } from "../../../../services/api";

const BrokerInstructions = ({
  broker,
  onContinue,
  userId,
  app_name = "DeeTrade",
  onAccountIdChange,
  isEditMode = false,
}) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState(null);

  useEffect(() => {
    if (accountId && onAccountIdChange) {
      onAccountIdChange(accountId);
    }
  }, [accountId, onAccountIdChange]);

  useEffect(() => {
    if (broker?.broker_id) {
      console.log("Original broker object:", broker);
      console.log("Broker ID received:", broker.broker_id);
      fetchBrokerInstructionsAndRedirectUrl(broker.broker_id);

      if (isEditMode) {
        setInstructions((prevInstructions) => ({
          ...editModeInstructions,
          redirectUrl: prevInstructions?.redirectUrl || "",
        }));
      }
    } else {
      setLoading(false);
      setInstructions(isEditMode ? editModeInstructions : defaultInstructions);
    }
  }, [broker, userId, isEditMode]);

  const fetchBrokerInstructionsAndRedirectUrl = async (brokerId) => {
    try {
      setLoading(true);
      setError(null);

      console.log(
        `Fetching instructions and redirect URL for broker: ${brokerId}`
      );

      const instructionsResponse = await services.brokerPlatforms.getBrokerInstructions(
        brokerId
      );

      if (instructionsResponse.data.redirect_url_needed) {
        try {
          let existingAccountId = null;
          if (isEditMode && broker?.account_id) {
            existingAccountId = broker.account_id;
            console.log(
              `Using existing account ID for redirect URL: ${existingAccountId}`
            );
          }

          const redirectResponse = await services.brokerPlatforms.generateRedirectUrl(
            brokerId,
            existingAccountId
          );

          console.log(
            `Redirect URL response for broker ${brokerId}:`,
            redirectResponse
          );

          if (redirectResponse && redirectResponse.data) {
            setRedirectUrl(redirectResponse.data.redirect_url);
            setAccountId(redirectResponse.data.account_id);
            console.log(
              "Redirect URL generated:",
              redirectResponse.data.redirect_url
            );
          }
        } catch (redirectErr) {
          console.error("Error generating redirect URL:", redirectErr);
        }
      }

      if (instructionsResponse && instructionsResponse.data) {
        console.log("Broker instructions data:", instructionsResponse.data);
        setInstructions(instructionsResponse.data);
      } else {
        console.warn("Response received but no data found");
        throw new Error("No data received from API");
      }
    } catch (err) {
      console.error("Error fetching broker information:", err);
      console.error("Full error object:", JSON.stringify(err, null, 2));
      setError(
        `Failed to load broker instructions: ${err.message || "Unknown error"}`
      );
      setInstructions(defaultInstructions);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage(message || "Copied to clipboard!");
    setOpenSnackbar(true);
  };

  const defaultInstructions = {
    title: isEditMode ? "Update API Credentials" : "Get API Credentials",
    steps: isEditMode
      ? [
          "Log in to your broker account",
          "Navigate to API or Developers section",
          "Find your existing API key",
          "You can either regenerate your API key or keep using the existing one",
          "If you regenerate, make sure to update both the API Key and Secret",
          "If regenerating and it requires a redirect URL, use the one provided below",
          "If you leave the API credentials blank, your current credentials will be preserved",
          "After updating, you'll need to re-verify your connection",
        ]
      : [
          "Log in to your broker account",
          "Navigate to API or Developers section",
          "Create a new API key or application",
          "Use the suggested Application Name provided below for easier identification",
          "If required, paste the exact Redirect URL provided below into your broker's API configuration",
          "Select all required permissions for trading (order placement, portfolio management, etc.)",
          "Make note of all credentials provided",
          "You'll need these details in the next step",
        ],
    helpText: isEditMode
      ? "Leave API credentials blank if you want to keep your current ones"
      : "Check your broker's documentation for specific instructions",
    app_name: "DeeTrade",
    redirect_url_needed: true,
  };

  const editModeInstructions = {
    title: "Update Broker Connection",
    steps: [
      "You can update your API key and secret for your broker account",
      "If you don't want to change your API credentials, leave those fields blank",
      "If you're regenerating your API key, you may need to use the redirect URL shown below",
      "Copy the redirect URL exactly as shown if your broker requires it for new API keys",
      "After updating, you'll need to verify your connection by logging in again",
      "This ensures that your new credentials are working properly",
    ],
    helpText:
      "Note: After updating, you'll need to authenticate again to ensure your connection works",
    app_name: app_name || "DeeTrade",
    redirect_url_needed: true,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
        <span className="ml-3 text-fg-2">
          Loading broker instructions...
        </span>
      </div>
    );
  }

  if (error && !instructions) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
        <div className="text-red-800 dark:text-red-300">{error}</div>
      </div>
    );
  }

  if (!instructions) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-fg-1 mb-2">
          {instructions.title}
        </h3>
      </div>
      {/* Step-by-Step Instructions */}
      <div className="bg-bg-3 dark:bg-bg-3/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--divider)]">
          <div className="flex items-center">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-3 h-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h4 className="font-medium text-fg-1">
              Step-by-Step Instructions
            </h4>
          </div>

          {instructions.url && (
            <a
              href={instructions.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm flex items-center"
            >
              Broker Site
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          )}
        </div>

        {/* Timeline-style steps */}
        <div className="space-y-3">
          {instructions.steps.map((step, index) => (
            <div key={index} className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                {index + 1}
              </div>
              <div className="flex-1">
                <p className="text-sm text-fg-2">
                  {typeof step === "string"
                    ? step.replace(
                        /DeeTrade|the provided app name|this name/g,
                        (match) =>
                          match === "DeeTrade" ? `"${app_name}"` : match
                      )
                    : step}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Configuration Details */}
      {instructions.redirect_url_needed && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-3 h-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h4 className="font-medium text-fg-1">
              API Configuration Details
            </h4>
          </div>

          {/* App Name and Redirect URL in same row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* App Name */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Application Name
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={app_name}
                  readOnly
                  className="flex-1 px-3 py-2 border border-[var(--divider)] rounded-l-md bg-bg-3 text-fg-1 text-sm"
                />
                <button
                  onClick={() =>
                    handleCopyToClipboard(
                      app_name,
                      "App name copied to clipboard!"
                    )
                  }
                  className="btn btn-secondary !rounded-l-none !border-l-0 !h-[38px]"
                  title="Copy App Name"
                >
                  <svg
                    className="w-4 h-4 text-fg-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-fg-2 mt-1">
                Use this name for identification
              </p>
            </div>

            {/* Redirect URL */}
            {instructions.redirect_url_needed && redirectUrl && (
              <div>
                <label className="block text-sm font-medium text-fg-2 mb-2">
                  Redirect URL
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={redirectUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-[var(--divider)] rounded-l-md bg-bg-3 text-fg-1 text-sm"
                  />
                  <button
                    onClick={() =>
                      handleCopyToClipboard(
                        redirectUrl,
                        "Redirect URL copied to clipboard!"
                      )
                    }
                    className="btn btn-secondary !rounded-l-none !border-l-0 !h-[38px]"
                    title="Copy Redirect URL"
                  >
                    <svg
                      className="w-4 h-4 text-fg-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-fg-2 mt-1">
                  Copy this URL exactly as shown
                </p>
              </div>
            )}
          </div>

          {/* Static IP — required by SEBI for any broker API order placement.
              Sourced from REACT_APP_STATIC_IP env so it's configurable per env,
              with a fallback to the current Oracle Reserved IP. */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Static IP{" "}
              <span className="text-xs text-fg-2">
                (SEBI-required — register as primary IP at the broker)
              </span>
            </label>
            <div className="flex">
              <input
                type="text"
                value={
                  process.env.REACT_APP_STATIC_IP || "140.245.242.62"
                }
                readOnly
                className="flex-1 px-3 py-2 border border-[var(--divider)] rounded-l-md bg-bg-3 text-fg-1 text-sm font-mono"
              />
              <button
                onClick={() =>
                  handleCopyToClipboard(
                    process.env.REACT_APP_STATIC_IP || "140.245.242.62",
                    "Static IP copied to clipboard!"
                  )
                }
                className="btn btn-secondary !rounded-l-none !border-l-0 !h-[38px]"
                title="Copy Static IP"
              >
                <svg
                  className="w-4 h-4 text-fg-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
            <p className="text-xs text-fg-2 mt-1">
              Add this as the primary static IP when generating your API key.
              All order placement traffic egresses from this IP.
            </p>
          </div>
        </div>
      )}
      {/* Help Text */}
      <div className="text-sm text-fg-2">
        {instructions.helpText}
      </div>
      {/* Notification */}
      {openSnackbar && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

export default React.memo(BrokerInstructions);
